import type { PackItem } from '../features/Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePage';

/* Turning a raw `/package/*` row into the `PackItem` the card components render.
 *
 * WHY THIS FILE EXISTS. The same mapping was written out three times — in
 * `RafflePage`, `EmailPromo` and `PartnerSignupUpgradePage` — each one module-local
 * and none exported, so a fourth page had no way to reuse it and every fix had to
 * be made in three places. The colour palette was duplicated alongside it.
 *
 * These are commercial numbers: `entries` is what a buyer is told they get and
 * `price` is what they are charged. Three copies of that conversion is three
 * chances for one screen to quote a figure the others do not.
 *
 * The `PackItem` type still lives in `RafflePage` because six files already import
 * it from there; moving it is a wider refactor than this needed to be.
 */

export type ApiPackage = {
  id: number;
  name: string;
  type: 'Standalone' | 'Subscription';
  description: string | null;
  imageUrl?: string | null;
  price: string | number;
  cards?: number | null;
  tokens?: number | null;
  entries?: number | null;
  Bonus?: {
    description?: string | null;
    extraCards?: number | null;
    extraEntries?: number | null;
    price?: string | number | null;
  } | null;
};

/* Indexed by position, so the nth pack is always the same colour on every screen
 * that renders it. Matches the palette `RafflePage` has used since the funnel
 * launched — changing the order here re-colours the live giveaway page too. */
export const PACK_DOT_COLOURS = [
  'linear-gradient(180deg,#B14CFF,#7B1BFF)', // Purple
  'linear-gradient(180deg,#7CFF3A,#3AAE12)', // Green
  'linear-gradient(180deg,#FF4A4A,#B30000)', // Red
  'linear-gradient(180deg,#FFD700,#C79A00)' // Gold
];

/** Raw subscription rows → the shape `SubscriptionUpgradeCards` expects. */
export const toPackSubscriptionItems = (pkgs: ApiPackage[]): PackItem[] =>
  (Array.isArray(pkgs) ? pkgs : []).map((p, idx) => ({
    id: String(p.id),
    title: p.name,
    price: typeof p.price === 'number' ? `${p.price}` : String(p.price),
    entries: Number(p.entries || 0),
    nfts: Number(p.cards || 0),
    tokens: Number(p.tokens || 0),
    description: p.description || '',
    dotColor: PACK_DOT_COLOURS[idx % PACK_DOT_COLOURS.length]
  }));

/* Cheapest first.
 *
 * `/package/subscription` orders by `createdAt`, which is the order the rows
 * happened to be inserted in — not an order a shopper would expect. Several of
 * the card components also derive badges from array POSITION, so leaving the API
 * order in place makes "Starter plan" land on whichever pack was created first. */
export const byPriceAscending = (items: PackItem[]): PackItem[] =>
  [...items].sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
